import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CheaperWayCTA } from "@/components/CheaperWayCTA";
import { ArrowUpRight, Utensils } from "lucide-react";

// The priority ladder: free food help first, purchase last.
const LADDER = [
  "Free right now", "Sign-up offer", "Rewards offer", "Food rescue / surplus",
  "Local food assistance", "SNAP / EBT / benefits", "Cheap eats", "Restaurant deal", "Normal purchase",
];

const SIGNUP_NAMES = /mcdonald|starbucks|chipotle|taco bell|chick-fil-a|dunkin|panera|burger king|rewards|perks/i;
const RESCUE_NAMES = /too good to go|flashfood|olio|freecycle|surplus|rescue|imperfect|misfits/i;
const HELP_NAMES = /211|snap|ebt|food bank|feeding|pantry|wic|benefit|community|meals/i;

function Card({ s }) {
  const url = s.affiliate_url || s.destination_url || s.official_url || s.application_url || "#";
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`hungry-source-${(s.name||'').toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
      className="group border hairline p-5 hover:border-[#EF3340] transition-colors flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <span className="font-display text-lg leading-tight">{s.name}</span>
        <ArrowUpRight className="w-4 h-4 text-stone-500 group-hover:text-[#EF3340] transition-colors shrink-0" />
      </div>
      <span className="font-label text-[10px] tracking-widest text-stone-500 mt-2">{s.subcategory || s.category}</span>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {(s.strategies || []).slice(0, 3).map((st) => (
          <span key={st} className="font-label text-[9px] tracking-widest text-[#EF3340] border border-[#EF3340]/40 px-2 py-0.5">{st}</span>
        ))}
      </div>
    </a>
  );
}

function Section({ eyebrow, title, note, items }) {
  if (!items.length) return null;
  return (
    <section className="mb-14" data-testid={`hungry-section-${eyebrow.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
      <div className="eyebrow mb-2">{eyebrow}</div>
      <h2 className="font-display text-2xl md:text-3xl leading-tight mb-1">{title}</h2>
      {note && <p className="font-serif text-sm text-stone-400 mb-5 max-w-2xl">{note}</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
        {items.map((s) => <Card key={s.id || s.name} s={s} />)}
      </div>
    </section>
  );
}

export default function Hungry() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sources", { params: { category: "Food" } })
      .then((r) => setSources(r.data.sources || []))
      .finally(() => setLoading(false));
  }, []);

  const signup = sources.filter((s) => SIGNUP_NAMES.test(s.name) || s.subcategory === "Sign-Up Offers");
  const rescue = sources.filter((s) => RESCUE_NAMES.test(s.name) || /surplus|free food/i.test(s.subcategory || ""));
  const help = sources.filter((s) => HELP_NAMES.test(s.name) || HELP_NAMES.test(s.subcategory || ""));
  const claimed = new Set([...signup, ...rescue, ...help].map((s) => s.id || s.name));
  const rest = sources.filter((s) => !claimed.has(s.id || s.name));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      <div className="flex items-center gap-3 mb-4">
        <Utensils className="w-5 h-5 text-[#EF3340]" />
        <div className="eyebrow">HUNGRY?</div>
      </div>
      <h1 className="font-display text-4xl md:text-7xl leading-[0.9] mb-4">GET FED FOR<br />LESS. OR FREE.</h1>
      <p className="font-serif text-xl text-stone-300 max-w-2xl mb-8">
        Before you tap "order," here's the ladder we run: free food help and sign-up freebies first,
        a paid menu last. What's the cheapest legitimate way to get fed right now?
      </p>

      <div className="flex flex-wrap gap-2 mb-14 border-y hairline py-5">
        {LADDER.map((step, i) => (
          <span key={step} className="font-label text-[10px] tracking-widest text-stone-400">
            <span className="text-[#EF3340]">{i + 1}.</span> {step.toUpperCase()}
            {i < LADDER.length - 1 && <span className="text-stone-600 ml-2">→</span>}
          </span>
        ))}
      </div>

      <div className="mb-8 border border-[#D4AF37]/40 bg-[#141217] px-5 py-4">
        <p className="font-label text-[10px] tracking-widest text-[#D4AF37]">
          $0 REQUIRED PURCHASE vs FREE WITH PURCHASE — offers change often. Always confirm the current
          offer and whether a purchase is required before you count on it.
        </p>
      </div>

      {loading ? (
        <p className="font-display text-2xl text-stone-500">Finding the cheaper way…</p>
      ) : (
        <>
          <Section eyebrow="FREE / ALMOST FREE" title="Sign-up & rewards freebies"
            note="Most chains give new app/rewards members a free or discounted item. Check the required purchase before you rely on it."
            items={signup} />
          <Section eyebrow="FOOD RESCUE" title="Surplus & rescued food"
            note="Restaurants and stores sell surplus food cheap at the end of the day." items={rescue} />
          <Section eyebrow="FOOD ASSISTANCE" title="Free community food & benefits"
            note="Need help finding help? These connect you to food banks, pantries, SNAP and local programs. No judgment." items={help} />
          <Section eyebrow="MORE FOOD SOURCES" title="Other cheaper-way food sources" items={rest} />
        </>
      )}

      <div className="mt-6"><CheaperWayCTA category="FOOD & GROCERIES" /></div>
    </div>
  );
}
